import axios from './api'
import { organizationContextStore } from 'shared/stores/organizationContextStore'

function withOrganizationContext(config = {}) {
	return {
		...config,
		headers: {
			...(config.headers || {}),
			...organizationContextStore.contextHeaders,
		},
	}
}

export function adminCreateNewCounselor(counselor) {
	return axios.post('/counseling/counselor', counselor)
}

export function adminGetOrgStudents() {
	return axios.get('/counseling/organization/students', withOrganizationContext())
}

export function adminSubmitOrgStudents(payload) {
	return axios.put('/counseling/organization/students', payload, withOrganizationContext())
}

/**
 * List organizations for platform CEO organization picker.
 */
export async function fetchPlatformOrganizations() {
	const response = await axios.get('/counseling/platform/organizations')
	return response.data
}

/**
 * List organization counselors (User Management).
 * @returns {Promise<{ organization_id: number, counselors: Array }>}
 */
export async function fetchOrganizationCounselors() {
	const response = await axios.get('/counseling/organization/counselors', withOrganizationContext())
	return response.data
}

/**
 * Assign/reassign organization students to a counsellor (User Management).
 * @param {{ counselorId: string, studentIds: string[] }} params
 * @returns {Promise<{ organization_id: number, counselor_id: string, assigned_count: number, student_ids: string[] }>}
 */
export async function assignOrganizationStudents({ counselorId, studentIds }) {
	const response = await axios.post(
		'/counseling/organization/students/assign',
		{
			counselor_id: counselorId,
			student_ids: studentIds,
		},
		withOrganizationContext(),
	)
	return response.data
}

/**
 * Unlink students from the User Management organization context.
 * @param {{ studentIds: string[] }} params
 * @returns {Promise<{ organization_id: number, removed_count: number, student_ids: string[] }>}
 */
export async function unlinkOrganizationStudents({ studentIds }) {
	const response = await axios.post(
		'/counseling/organization/students/unlink',
		{
			student_ids: studentIds,
		},
		withOrganizationContext(),
	)
	return response.data
}

/**
 * Link an existing student account to the User Management organization context.
 * @param {{ student_id: string, assign_to_me?: boolean }} payload
 */
export async function linkOrganizationStudent(payload) {
	const response = await axios.put('/counseling/organization/students', payload, withOrganizationContext())
	return response.data
}

/**
 * List assignable organization roles (User Management).
 */
export async function fetchOrganizationRoles() {
	const response = await axios.get('/counseling/organization/roles', withOrganizationContext())
	return response.data
}

/**
 * Create a counsellor in the User Management organization context.
 */
export async function createOrganizationCounselor(payload) {
	const response = await axios.post('/counseling/organization/counselors', payload, withOrganizationContext())
	return response.data
}

/**
 * Update a counsellor access level (User Management).
 */
export async function updateOrganizationCounselorRole({ counselorId, roleId }) {
	const response = await axios.patch(
		`/counseling/organization/counselors/${counselorId}`,
		{
			role_id: roleId,
		},
		withOrganizationContext(),
	)
	return response.data
}

/**
 * Remove a counsellor from the User Management organization context.
 */
export async function removeOrganizationCounselor(counselorId) {
	const response = await axios.delete(
		`/counseling/organization/counselors/${counselorId}`,
		withOrganizationContext(),
	)
	return response.data
}

/**
 * Look up an existing student by email (User Management).
 */
export async function lookupOrganizationStudentByEmail(email) {
	const response = await axios.get('/counseling/organization/students/lookup', {
		...withOrganizationContext(),
		params: { email },
	})
	return response.data
}
